import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { Shield, Loader2, RefreshCw, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { queryClient, setToken } from "@/lib/queryClient";

export default function VerifyEmailPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const params = new URLSearchParams(window.location.search);
  const email = params.get("email") || sessionStorage.getItem("pendingVerifyEmail") || "";
  const invitationToken = params.get("invitationToken") || sessionStorage.getItem("pendingInvitationToken") || "";
  const returnTo = params.get("returnTo") || "";
  const selectedPlan = params.get("plan") || "";

  useEffect(() => {
    if (!email) setLocation("/signup");
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  function handleChange(index: number, value: string) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 5) inputRefs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) {
      setOtp(text.split(""));
      inputRefs.current[5]?.focus();
    }
  }

  async function handleVerify() {
    const code = otp.join("");
    if (code.length < 6) {
      toast({ title: "Enter all 6 digits", variant: "destructive" });
      return;
    }
    setIsVerifying(true);
    try {
      const res = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: code }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast({ title: "Verification failed", description: json.error?.message || json.message || "Something went wrong. Please try again.", variant: "destructive" });
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }
      sessionStorage.removeItem("pendingVerifyEmail");
      sessionStorage.removeItem("pendingInvitationToken");
      if (json.token) setToken(json.token);
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      const choosePlanUrl = selectedPlan ? `/choose-plan?plan=${selectedPlan}` : "/choose-plan";
      const destination = invitationToken ? `/accept-invitation/${invitationToken}` : (returnTo || choosePlanUrl);
      setLocation(destination);
    } catch {
      toast({ title: "Verification failed", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleResend() {
    if (countdown > 0) return;
    setIsResending(true);
    try {
      const res = await fetch("/api/auth/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast({ title: "Failed to resend", description: json.error?.message || json.message || "Something went wrong. Please try again.", variant: "destructive" });
        return;
      }
      toast({ title: "Code sent!", description: "Check your inbox for a new verification code." });
      setCountdown(60);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } catch {
      toast({ title: "Failed to resend", description: "Something went wrong. Please try again.", variant: "destructive" });
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--restore-dark-bg)" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center space-x-3 mb-10 justify-center">
          <div className="rounded-xl p-2" style={{ backgroundColor: "var(--restore-accent)" }}>
            <Shield className="w-6 h-6 text-gray-900" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">RestoreAI</h1>
            <p className="text-sm" style={{ color: "var(--restore-text-muted)" }}>AI-Powered Restoration Intelligence</p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-700/50 bg-gray-900/80 p-8">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-500/30">
              <Mail className="w-7 h-7 text-blue-400" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white text-center mb-2">Check your email</h2>
          <p className="text-gray-400 text-center text-sm mb-1">We sent a 6-digit code to</p>
          <p className="text-white font-medium text-center mb-4" style={{ color: "var(--restore-accent)" }}>{email}</p>
          {invitationToken && (
            <p className="text-xs text-green-400 text-center mb-6">
              After verifying, you'll be automatically redirected to join your team.
            </p>
          )}

          <div className="flex gap-3 justify-center mb-8" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <Input
                key={i}
                ref={el => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-2xl font-bold bg-gray-800 border-gray-600 text-white focus:border-green-400 focus:ring-green-400"
                style={{ caretColor: "transparent" }}
              />
            ))}
          </div>

          <Button
            onClick={handleVerify}
            disabled={isVerifying || otp.join("").length < 6}
            className="w-full text-gray-900 font-semibold h-11 text-base mb-4"
            style={{ backgroundColor: "var(--restore-accent)" }}
          >
            {isVerifying ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify Email"}
          </Button>

          <div className="text-center">
            <p className="text-gray-400 text-sm mb-2">Didn't receive the code?</p>
            <Button
              variant="ghost"
              onClick={handleResend}
              disabled={isResending || countdown > 0}
              className="text-blue-400 hover:text-blue-300 h-auto p-0"
            >
              {isResending ? (
                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Sending...</>
              ) : countdown > 0 ? (
                `Resend in ${countdown}s`
              ) : (
                "Resend code"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
